import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch } from 'vue';
import { useExportDialogStore } from '@/store/exportDialog';
import { configRegistry } from '@/lib/configRegistry';
import { usePreferencesStore } from '@/store/preferences';
import { useLayoutStore } from '@/store/layout';


export default /*@__PURE__*/_defineComponent({
  __name: 'ExportDialog',
  setup(__props) {

const exportDialogStore = useExportDialogStore();
const preferencesStore = usePreferencesStore();
const layoutStore = useLayoutStore();

const isOpen = computed(() => exportDialogStore.isOpen);
const moduleName = computed(() => exportDialogStore.moduleName);
const fileName = computed(() => exportDialogStore.fileName);
const exportCsvFn = computed(() => exportDialogStore.exportCsv);
const loading = ref(false);

const headers = ref<any[]>([]);
const selectedHeaders = ref<string[]>([]);
const exportScope = ref<'paged'|'filtered'|'all'>('paged');

const layoutParams = computed(() => {
  if (!moduleName.value) return {};
  const layout = layoutStore.getLayout(moduleName.value);
  if (!layout) return {};
  return {
    page: layout.page,
    itemsPerPage: layout.itemsPerPage,
    sort: layout.sortBy ? (layout.direction === 'desc' ? `-${layout.sortBy}` : layout.sortBy) : '',
    filters: layout.filters || {},
    totalItems: layout.totalItems
  };
});

watch(moduleName, async (modName) => {
  if (!modName) return;
  const loader = configRegistry[modName];
  if (!loader) return;
  const mod = await loader();
  headers.value = mod.HEADERS.filter((h:any) => !!h.title);

  const userPrefs = preferencesStore.preferences?.[modName]?.headers?.data || [];
  selectedHeaders.value = userPrefs.length
    ? userPrefs.map((h: any) => typeof h === 'string' ? h : h.value)
    : headers.value.map((h: any) => h.value);
}, { immediate: true });

function closeDialog() {
  exportDialogStore.closeDialog();
}

const exportColumns = computed(() =>
  headers.value.filter(h => selectedHeaders.value.includes(h.value))
    .map(h => ({ title: h.title, value: h.value }))
);

function flatFilters(filters: Record<string, any> = {}) {
  if (!filters) return {};
  return Object.entries(filters)
    .filter(([_, v]) => v != null && v !== '')
    .reduce((acc, [k, v]) => {
      acc[`filter[${k}]`] = v;
      return acc;
    }, {} as Record<string, any>);
}

async function exportCSV() {
  if (!exportCsvFn.value) {
    alert('No hay exportador configurado');
    return;
  }
  loading.value = true;

  const columns = exportColumns.value;
  const layout = layoutParams.value;
  let query: Record<string, any> = {};
  if (exportScope.value === 'paged') {
    query = {
      page: layout.page,
      itemsPerPage: layout.itemsPerPage,
      sort: layout.sort,
      ...flatFilters(layout.filters),
    };
  } else if (exportScope.value === 'filtered') {
    query = {
      page: 1,
      itemsPerPage: layout.totalItems,
      sort: layout.sort,
      ...flatFilters(layout.filters),
    };
  } else if (exportScope.value === 'all') {
    query = {
      page: 1,
      itemsPerPage: layout.totalItems,
      sort: layout.sort
    };
  }
  const searchParams = new URLSearchParams(query).toString();

  try {
    const response = await exportCsvFn.value(searchParams, { columns });

    // Asegúrate de esto:
    let blob;
    if (response.data instanceof Blob) {
      blob = response.data;
    } else {
      // Si llega como string, lo convertimos a Uint8Array (más seguro que pasar a Blob directo)
      const byteCharacters = atob(response.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName.value + '.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    closeDialog();
  } catch (e) {
    console.error(e);
  }finally{
    loading.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: isOpen.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isOpen).value = $event)),
    "max-width": "540"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "px-3 py-2" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "mx-3 mt-2" }, {
            default: _withCtx(() => _cache[3] || (_cache[3] = [
              _createTextVNode("Exportar CSV")
            ])),
            _: 1,
            __: [3]
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, { cols: "12" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_autocomplete, {
                        modelValue: selectedHeaders.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedHeaders).value = $event)),
                        items: headers.value,
                        "item-title": "title",
                        "item-value": "value",
                        label: "Columnas a exportar",
                        multiple: "",
                        chips: "",
                        clearable: "",
                        "return-object": false
                      }, null, 8, ["modelValue", "items"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, { cols: "12" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_radio_group, {
                        modelValue: exportScope.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((exportScope).value = $event)),
                        row: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_radio, {
                            label: "Lo que veo (paginado + filtros)",
                            value: "paged"
                          }),
                          _createVNode(_component_v_radio, {
                            label: "Todos los datos filtrados",
                            value: "filtered"
                          }),
                          _createVNode(_component_v_radio, {
                            label: "Todo (ignorar filtros/paginación)",
                            value: "all"
                          })
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                text: "",
                onClick: closeDialog
              }, {
                default: _withCtx(() => _cache[4] || (_cache[4] = [
                  _createTextVNode("Cancelar")
                ])),
                _: 1,
                __: [4]
              }),
              _createVNode(_component_v_btn, {
                color: "primary",
                onClick: exportCSV,
                loading: loading.value
              }, {
                default: _withCtx(() => _cache[5] || (_cache[5] = [
                  _createTextVNode("Exportar")
                ])),
                _: 1,
                __: [5]
              }, 8, ["loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})